export interface JobSchedule {
  id: number;
  jobId: number;
  milestoneType: MilestoneType;
  milestoneName: string;
  description?: string;
  
  // Dates
  scheduledDate?: string;
  actualDate?: string;
  originalScheduledDate?: string;
  
  // Status tracking
  status?: ScheduleStatus;
  progressPercentage?: number;
  
  // Dependencies
  dependsOnMilestoneId?: number | null;
  leadTimeDays?: number;
  
  // Responsibility
  responsibleParty?: string;
  responsibleUserId?: number | null;
  
  // Notes
  notes?: string;
  completionNotes?: string;
  
  // Tracking fields
  varianceDays?: number;
  isCriticalPath?: boolean;
  isVisible?: boolean;
  sortOrder?: number;
  
  // Metadata
  createdBy?: number;
  updatedBy?: number;
  createdAt: string;
  updatedAt: string;
}

export type MilestoneType = 
  | 'projectKickoff'
  | '4x4Review'
  | 'pilotPanelReview'
  | 'sprayFoamDate'
  | 'glassInstall'
  | 'projectCompletion'
  | 'designApproval'
  | 'shopDrawingSubmission'
  | 'shopDrawingApproval'
  | 'productionStart'
  | 'firstPanelCast'
  | 'qualityControlReview'
  | 'panelCureComplete'
  | 'shippingStart'
  | 'siteDelivery'
  | 'erectionStart'
  | 'erectionComplete'
  | 'punchList'
  | 'finalInspection'
  | 'permitSubmission'
  | 'permitApproval'
  | 'foundationComplete'
  | 'structuralComplete'
  | 'weatherproofingComplete'
  | 'custom';

export type ScheduleStatus = 
  | 'upcoming'
  | 'pending'
  | 'inProgress'
  | 'completed'
  | 'delayed'
  | 'onHold'
  | 'cancelled';

export interface JobScheduleCreate {
  jobId: number;
  milestoneType: MilestoneType;
  milestoneName: string;
  description?: string;
  scheduledDate?: string;
  actualDate?: string;
  originalScheduledDate?: string;
  status?: ScheduleStatus;
  progressPercentage?: number;
  dependsOnMilestoneId?: number | null;
  leadTimeDays?: number;
  responsibleParty?: string;
  responsibleUserId?: number | null;
  notes?: string;
  completionNotes?: string;
  varianceDays?: number;
  isCriticalPath?: boolean;
  isVisible?: boolean;
  sortOrder?: number;
}

export interface JobSchedulePatch extends Partial<Omit<JobScheduleCreate, 'jobId'>> {}

export interface JobScheduleQuery {
  id?: number;
  jobId?: number;
  milestoneType?: MilestoneType;
  status?: ScheduleStatus;
  isCriticalPath?: boolean;
  isVisible?: boolean;
  responsibleUserId?: number;
  createdBy?: number;
  updatedBy?: number;
  
  // Date range queries
  scheduledDateFrom?: string;
  scheduledDateTo?: string;
  actualDateFrom?: string;
  actualDateTo?: string;
  
  // Feathers query syntax
  $limit?: number;
  $skip?: number;
  $sort?: Record<string, 1 | -1>;
  $select?: string[];
}

export const milestoneDisplayNames: Record<MilestoneType, string> = {
  'projectKickoff': 'Project Kickoff',
  '4x4Review': '4x4 Review',
  'pilotPanelReview': 'Pilot Panel Review',
  'sprayFoamDate': 'Spray Foam Date',
  'glassInstall': 'Glass Install',
  'projectCompletion': 'Project Completion',
  'designApproval': 'Design Approval',
  'shopDrawingSubmission': 'Shop Drawing Submission',
  'shopDrawingApproval': 'Shop Drawing Approval',
  'productionStart': 'Production Start',
  'firstPanelCast': 'First Panel Cast',
  'qualityControlReview': 'Quality Control Review',
  'panelCureComplete': 'Panel Cure Complete',
  'shippingStart': 'Shipping Start',
  'siteDelivery': 'Site Delivery',
  'erectionStart': 'Erection Start',
  'erectionComplete': 'Erection Complete',
  'punchList': 'Punch List',
  'finalInspection': 'Final Inspection',
  'permitSubmission': 'Permit Submission',
  'permitApproval': 'Permit Approval',
  'foundationComplete': 'Foundation Complete',
  'structuralComplete': 'Structural Complete',
  'weatherproofingComplete': 'Weatherproofing Complete',
  'custom': 'Custom Milestone'
};

export const getStatusColor = (status?: ScheduleStatus): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
  switch (status) {
    case 'upcoming':
      return 'default';
    case 'pending':
      return 'info';
    case 'inProgress':
      return 'warning';
    case 'completed':
      return 'success';
    case 'delayed':
      return 'error';
    case 'onHold':
      return 'warning';
    case 'cancelled':
      return 'default';
    default:
      return 'default';
  }
};

export const getStatusIcon = (status?: ScheduleStatus): string => {
  switch (status) {
    case 'completed':
      return 'CheckCircle';
    case 'inProgress':
      return 'Pending';
    case 'delayed':
      return 'Error';
    case 'onHold':
      return 'Pause';
    case 'cancelled':
      return 'Cancel';
    case 'pending':
      return 'HourglassEmpty';
    default:
      return 'RadioButtonUnchecked';
  }
};